import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faFileCsv,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faSpinner,
  faUsers,
  faDownload,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { userImportService } from '../../services/userImportService';
import './UserImportPanel.css';

/**
 * Bulk user import panel.
 *
 * Flow:
 *   1. Admin uploads CSV with columns: firstName, lastName, email
 *   2. Parsed client-side and shown as JSON preview
 *   3. Admin clicks Import → backend creates users in Auth0 with type='full'
 *      and triggers Auth0 "Change Password" email as the welcome email
 */
const UserImportPanel = () => {
  const [parsedUsers, setParsedUsers] = useState([]);
  const [fileName, setFileName] = useState('');
  const [parseErrors, setParseErrors] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const resetState = () => {
    setParsedUsers([]);
    setFileName('');
    setParseErrors([]);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setFileName(file.name);
    setImportResult(null);
    setParseErrors([]);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const { users, errors } = parseCsv(text);
      setParsedUsers(users);
      setParseErrors(errors);
      if (users.length === 0) {
        toast.warning('No valid users found in CSV');
      } else {
        toast.info(`Parsed ${users.length} user${users.length !== 1 ? 's' : ''}`);
      }
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const csv = [
      'firstName,lastName,email',
      'Jane,Murphy,jane.murphy@example.com',
      'John,Kelly,john.kelly@example.com',
    ].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'user-import-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (parsedUsers.length === 0) {
      toast.warning('No users to import');
      return;
    }

    if (
      !window.confirm(
        `Import ${parsedUsers.length} user${parsedUsers.length !== 1 ? 's' : ''} into Auth0? Each will receive a welcome email with a link to set their password.`,
      )
    ) {
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const result = await userImportService.importUsers(parsedUsers);
      setImportResult(result);
      toast.success(
        `Import complete: ${result.created} created, ${result.skipped} skipped, ${result.failed} failed`,
      );
    } catch (error) {
      const isTimeout =
        error.code === 'ECONNABORTED' ||
        /timeout/i.test(error.message || '') ||
        !error.response;
      if (isTimeout) {
        toast.warning(
          'The import is taking longer than expected and is still running on the server. ' +
            'Users are being created in the background — check Auth0 in a few minutes. ' +
            'You can safely re-upload the same file afterwards; users that already exist are skipped.',
          { autoClose: false },
        );
      } else {
        toast.error(
          `Import failed: ${error.response?.data?.message || error.message}`,
        );
      }
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="user-import-panel">
      <div className="user-import-header">
        <h2>
          <FontAwesomeIcon icon={faUsers} /> Bulk User Import
        </h2>
        <p className="user-import-description">
          Create multiple member accounts at once from a spreadsheet. Upload a
          CSV, review the users found in it, then import — the three steps below
          walk you through it.
        </p>
      </div>

      {/* Step 1: Upload */}
      <div className="import-step">
        <h3>Step 1 — Upload CSV</h3>
        <p className="step-help">
          <FontAwesomeIcon icon={faInfoCircle} /> Your file needs the columns{' '}
          <code>firstName</code>, <code>lastName</code>, <code>email</code> (one
          user per row). Only <strong>CSV</strong> is accepted — if your list is
          in Excel, use <em>File → Save As → CSV (Comma delimited)</em> to
          convert it.{' '}
          <button
            type="button"
            className="template-link"
            onClick={handleDownloadTemplate}
          >
            <FontAwesomeIcon icon={faDownload} /> Download a template
          </button>
        </p>
        <div className="upload-controls">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
            disabled={isImporting}
            id="csvFileInput"
            style={{ display: 'none' }}
          />
          <label htmlFor="csvFileInput" className="upload-button">
            <FontAwesomeIcon icon={faUpload} /> Choose CSV File
          </label>
          {fileName && (
            <span className="file-name">
              <FontAwesomeIcon icon={faFileCsv} /> {fileName}
            </span>
          )}
          {parsedUsers.length > 0 && (
            <button
              onClick={resetState}
              className="reset-button"
              disabled={isImporting}
            >
              Clear
            </button>
          )}
        </div>

        {parseErrors.length > 0 && (
          <div className="parse-errors">
            <strong>
              <FontAwesomeIcon icon={faExclamationTriangle} /> Skipped {parseErrors.length} row{parseErrors.length !== 1 ? 's' : ''} during parsing:
            </strong>
            <ul>
              {parseErrors.slice(0, 10).map((err, idx) => (
                <li key={idx}>
                  Row {err.row}: {err.reason}
                </li>
              ))}
              {parseErrors.length > 10 && (
                <li>... and {parseErrors.length - 10} more</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Step 2: Preview */}
      <div className={`import-step${parsedUsers.length === 0 ? ' is-pending' : ''}`}>
        <h3>
          Step 2 — Preview
          {parsedUsers.length > 0
            ? ` (${parsedUsers.length} user${parsedUsers.length !== 1 ? 's' : ''})`
            : ''}
        </h3>
        {parsedUsers.length > 0 ? (
          <>
            <p className="step-help">
              Check the names and email addresses below before importing. If
              anything looks wrong, fix your CSV and upload it again.
            </p>
            <div className="json-preview">
              <pre>{JSON.stringify(parsedUsers, null, 2)}</pre>
            </div>
          </>
        ) : (
          <p className="step-placeholder">
            Once you upload a CSV, the users found in the file appear here so you
            can check the names and emails before importing.
          </p>
        )}
      </div>

      {/* Step 3: Import */}
      <div className={`import-step${parsedUsers.length === 0 ? ' is-pending' : ''}`}>
        <h3>Step 3 — Import</h3>
        <p className={parsedUsers.length > 0 ? 'step-help' : 'step-placeholder'}>
          After you review the preview, importing creates each user in Auth0 as a{' '}
          <strong>full</strong> account with their email already verified, then
          sends them a welcome email with a link to set their password.
        </p>
        {parsedUsers.length > 0 && (
          <button
            onClick={handleImport}
            className="import-button"
            disabled={isImporting}
          >
            {isImporting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Importing...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} /> Import{' '}
                {parsedUsers.length} user
                {parsedUsers.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        )}
      </div>

      {/* Results */}
      {importResult && (
        <div className="import-results">
          <h3>Results</h3>
          <div className="result-summary">
            <span className="result-stat created">
              <FontAwesomeIcon icon={faCheckCircle} /> Created:{' '}
              {importResult.created}
            </span>
            <span className="result-stat skipped">
              <FontAwesomeIcon icon={faExclamationTriangle} /> Skipped:{' '}
              {importResult.skipped}
            </span>
            <span className="result-stat failed">
              <FontAwesomeIcon icon={faTimesCircle} /> Failed:{' '}
              {importResult.failed}
            </span>
          </div>

          <div className="result-table-wrapper">
            <table className="result-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {(importResult.results || []).map((r, idx) => (
                  <tr key={idx} className={`status-${r.status}`}>
                    <td>{r.email}</td>
                    <td>{r.status}</td>
                    <td>{r.error || (r.userId ? r.userId : '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Parse CSV text into an array of user objects.
 * Supports header row with any case/whitespace variation of:
 *   firstName, lastName, email
 * (also accepts "First Name", "Last Name", "Email").
 */
function parseCsv(text) {
  const users = [];
  const errors = [];

  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return { users, errors };

  // Parse header
  const header = splitCsvLine(lines[0]).map((h) =>
    h.trim().toLowerCase().replace(/\s+/g, ''),
  );
  const idxFirst = header.findIndex((h) => h === 'firstname' || h === 'first');
  const idxLast = header.findIndex((h) => h === 'lastname' || h === 'last' || h === 'surname');
  const idxEmail = header.findIndex((h) => h === 'email' || h === 'emailaddress');

  if (idxFirst === -1 || idxLast === -1 || idxEmail === -1) {
    errors.push({
      row: 1,
      reason: 'Header must include firstName, lastName, and email columns',
    });
    return { users, errors };
  }

  const seen = new Set();

  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i]);
    const firstName = (cols[idxFirst] || '').trim();
    const lastName = (cols[idxLast] || '').trim();
    const email = (cols[idxEmail] || '').trim().toLowerCase();

    if (!email || !firstName || !lastName) {
      errors.push({ row: i + 1, reason: 'Missing required field' });
      continue;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.push({ row: i + 1, reason: `Invalid email: ${email}` });
      continue;
    }
    if (seen.has(email)) {
      errors.push({ row: i + 1, reason: `Duplicate in CSV: ${email}` });
      continue;
    }
    seen.add(email);

    users.push({ firstName, lastName, email });
  }

  return { users, errors };
}

/**
 * Minimal CSV line splitter that handles double-quoted fields with commas inside.
 */
function splitCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

export default UserImportPanel;
